import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import {
  Users,
  HeartHandshake,
  CalendarDays,
  BookOpen,
  ArrowRight,
  MapPin,
  Mail,
} from 'lucide-react';
import { heroPhoto, storyPhotos, photographyCredit } from '../config/siteImages';

// Four evidence-based proof points (see redesign/LANDING_PLAN.md).
const stats = [
  { value: '100+', label: 'Members' },
  { value: '2015', label: 'Founded' },
  { value: '8', label: 'HK universities' },
  { value: '10 yrs', label: 'in 2025' },
];

// The portal's real offerings, so a prospective member knows what joining gives them.
const offerings = [
  { icon: Users, title: 'Member directory', body: 'Find fellow Ghanaian scholars by university and research field.' },
  { icon: HeartHandshake, title: 'Mentorship', body: 'Be matched with a mentor, or offer guidance as one.' },
  { icon: CalendarDays, title: 'Events and the AGM', body: 'RSVP to seminars and socials, and take part in how AGSHK is run.' },
  { icon: BookOpen, title: 'Resources', body: 'Scholarships, templates, and guidance for new arrivals in Hong Kong.' },
];

// WebP with JPEG fallback, used for both the hero and the story cards.
const Picture = ({ photo, className, imgClassName, loading = 'lazy' }) => (
  <picture className={className}>
    <source srcSet={photo.webp} type="image/webp" />
    <img
      src={photo.jpg}
      alt={photo.alt}
      loading={loading}
      decoding="async"
      className={imgClassName}
    />
  </picture>
);

const Landing = () => {
  return (
    <div className="min-h-screen bg-ags-bg">
      <Navbar />

      {/* Hero: full-bleed members photo under a navy scrim (Direction 3 warmth, Direction 2 structure) */}
      <section className="relative overflow-hidden" aria-labelledby="hero-heading">
        <Picture
          photo={heroPhoto}
          loading="eager"
          className="absolute inset-0 block"
          imgClassName="w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0 bg-gradient-to-br from-ags-blue/95 via-ags-blue/80 to-ags-blue/55"
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28 lg:py-32">
          <div className="max-w-2xl">
            <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-ags-gold mb-5">
              Association of Ghanaian Scholars in Hong Kong
            </p>
            <h1
              id="hero-heading"
              className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight tracking-tight mb-6"
            >
              A scholarly community, far from home.
            </h1>
            <p className="text-lg md:text-xl text-white/85 leading-relaxed mb-9 max-w-xl">
              Ghanaian postgraduates, researchers, and alumni across Hong Kong's universities,
              building friendship, mentorship, and the practical support that makes studying far
              from home easier.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
                className="w-full sm:w-auto bg-ags-gold text-ags-blue hover:bg-ags-gold/90 font-semibold focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                <Link to="/apply" data-testid="hero-apply-btn">
                  Apply for membership
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white/40 bg-transparent text-white hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                <Link to="/login" data-testid="hero-signin-btn">
                  Member sign in
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* From the President */}
      <section className="bg-ags-paper border-b border-slate-200" aria-labelledby="president-heading">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <p
            id="president-heading"
            className="text-xs font-semibold uppercase tracking-[0.18em] text-ags-text-secondary mb-4"
          >
            From the President
          </p>
          <div className="w-12 h-0.5 bg-ags-gold mb-7" aria-hidden="true" />
          <blockquote className="font-heading text-xl md:text-2xl lg:text-3xl leading-relaxed text-ags-blue">
            Welcome to the Association of Ghanaian Scholars in Hong Kong. For ten years, AGSHK has
            been the home of Ghanaian postgraduates, researchers, and scholars across Hong Kong's
            universities, a place to find community, mentorship, and connection. Whether you are a
            new arrival or a long-standing member, this portal is yours.
          </blockquote>
          <p className="mt-7 text-sm text-ags-text-secondary">
            <span className="font-semibold text-ags-text">Joseph Maxwell Asamoah</span>
            <br />
            President, AGSHK
          </p>
        </div>
      </section>

      {/* Stats band */}
      <section className="bg-white border-b border-slate-200" aria-label="AGSHK at a glance">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-heading text-3xl md:text-4xl font-bold text-ags-blue">{s.value}</div>
                <div className="mt-1 text-sm text-ags-text-secondary">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What membership gives you */}
      <section className="bg-ags-bg" aria-labelledby="offerings-heading">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ags-text-secondary mb-3">
              Membership
            </p>
            <h2 id="offerings-heading" className="font-heading text-3xl md:text-4xl text-ags-text tracking-tight">
              What membership gives you
            </h2>
            <p className="mt-4 text-ags-text-secondary">
              Everything below lives inside the members portal, ready the day you are approved.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {offerings.map((o) => (
              <div
                key={o.title}
                className="bg-white border border-slate-200 rounded-md p-6 transition-shadow hover:shadow-md"
              >
                <div className="w-11 h-11 rounded-md bg-ags-blue/5 flex items-center justify-center mb-4">
                  <o.icon className="w-5 h-5 text-ags-blue" strokeWidth={1.75} aria-hidden="true" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-ags-text mb-2">{o.title}</h3>
                <p className="text-sm text-ags-text-secondary leading-relaxed">{o.body}</p>
                <div className="mt-4 w-7 h-0.5 bg-ags-gold" aria-hidden="true" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stories strip */}
      <section className="bg-white border-y border-slate-200" aria-labelledby="stories-heading">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ags-text-secondary mb-3">
              Our people
            </p>
            <h2 id="stories-heading" className="font-heading text-3xl md:text-4xl text-ags-text tracking-tight">
              Scenes from the community
            </h2>
            <p className="mt-4 text-ags-text-secondary">
              Graduations, panels, and gatherings, the moments membership is really about.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {storyPhotos.map((p) => (
              <figure key={p.title} className="group bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="aspect-[3/2] overflow-hidden bg-ags-subtle">
                  <Picture
                    photo={p}
                    imgClassName="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                  />
                </div>
                <figcaption className="p-5">
                  <h3 className="font-heading text-base font-semibold text-ags-text">{p.title}</h3>
                  <p className="mt-1 text-sm text-ags-text-secondary">{p.blurb}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-ags-blue" aria-labelledby="cta-heading">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 text-center">
          <div className="w-12 h-0.5 bg-ags-gold mx-auto mb-7" aria-hidden="true" />
          <h2 id="cta-heading" className="font-heading text-3xl md:text-4xl text-white tracking-tight">
            You belong here
          </h2>
          <p className="mt-4 text-white/75 max-w-2xl mx-auto">
            Open to current students, researchers, and alumni connected to Hong Kong's universities.
            It takes a few minutes to apply.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row justify-center gap-4">
            <Button
              asChild
              size="lg"
              className="w-full sm:w-auto bg-ags-gold text-ags-blue hover:bg-ags-gold/90 font-semibold focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              <Link to="/apply" data-testid="cta-apply-btn">
                Apply for membership
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-white/40 bg-transparent text-white hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              <Link to="/login" data-testid="cta-signin-btn">
                Member sign in
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-ags-paper border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <img src="/agshk-logo-transparent.png" alt="" className="w-8 h-8 rounded-sm object-contain" />
                <span className="font-heading font-bold text-ags-blue">AGS-HK</span>
              </div>
              <p className="text-sm text-ags-text-secondary max-w-sm">
                Association of Ghanaian Scholars in Hong Kong. Connecting Ghanaian scholars globally.
              </p>
            </div>
            <div>
              <h2 className="font-heading text-sm font-semibold text-ags-text mb-4">Quick links</h2>
              <ul className="space-y-2 text-sm text-ags-text-secondary">
                <li><Link to="/about" className="hover:text-ags-blue">About</Link></li>
                <li><Link to="/portal" className="hover:text-ags-blue">Members portal</Link></li>
                <li><Link to="/apply" className="hover:text-ags-blue">Apply</Link></li>
                <li><Link to="/contact" className="hover:text-ags-blue">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h2 className="font-heading text-sm font-semibold text-ags-text mb-4">Contact</h2>
              <ul className="space-y-2 text-sm text-ags-text-secondary">
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" aria-hidden="true" /> Hong Kong
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" aria-hidden="true" />
                  <a href="mailto:info@ghanascholarshk.org" className="hover:text-ags-blue">info@ghanascholarshk.org</a>
                </li>
                <li>
                  <a href="https://ghanascholarshk.org" className="hover:text-ags-blue">ghanascholarshk.org</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-ags-text-secondary">
            <p>© {new Date().getFullYear()} Association of Ghanaian Scholars in Hong Kong. All rights reserved.</p>
            <p>{photographyCredit}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
